import {
	AccessManagementProvider,
	useAccessManagement,
} from "@valenzinsights/access-management-component";
import { adAuthConfig, endpoints } from "@valenzinsights/app-config";
import { LocalStorageKeys, RouteConstants } from "@valenzinsights/data";
import { Error401, Error403 } from "@valenzinsights/shared-pages";

import React, { Suspense, lazy, useEffect, useState } from "react";

import { useMsal } from "@azure/msal-react";
import {
	AuthProvider,
	HeaderVariant,
	IAuthenticationOptions,
	IUserInfoItem,
} from "@valenzinsights/react-core";
import { useNavigate } from "react-router";

import "./assets/css/App.css";
import {
	BackgroundWrapper,
	Logo,
	LogoSmall,
	ValanzLoader,
	ValanzLoaderComponent,
} from "./components";
import { layoutConfig } from "./config";
import {
	cutomOrangeTheme,
	homeMenuItems,
	itemData,
	profileListItem,
} from "./data";
import { LoginRoutes, routes, standaloneRoutes } from "./routes";

const appVersion = { text: "Valenz", version: "v 1.0" };

const LazyMuiReactBootstrap = lazy(() =>
	import("@valenzinsights/react-core").then((module) => ({
		default: module.MuiReactBootstrap,
	})),
);
const LazyAppLayout = lazy(() =>
	import("@valenzinsights/react-core").then((module) => ({
		default: module.AppLayout,
	})),
);

const App = () => {
	const tokenEndpoint = `${endpoints.backendApiUrl}/token`;

	const authenticationOptions: IAuthenticationOptions = {
		authProvider: AuthProvider.AzureAd,
		authConfig: adAuthConfig?.config,
		unAuthenticatedRoutes: LoginRoutes,
	};

	const AccessManagement: React.FC<React.PropsWithChildren<{}>> = ({
		children,
	}) => {
		const redirect = useNavigate();
		const { instance } = useMsal();

		return (
			<AccessManagementProvider
				instance={instance}
				apiEndpointUrl={tokenEndpoint}
				appTokenStorageKey={LocalStorageKeys.RoleManagement}
				autoRefreshToken={true}
				unauthorisedRedirect={(status) => {
					if (status === 401) redirect(RouteConstants.ErrorUnauthorized);
					else redirect(RouteConstants.ErrorUserNotFound);
				}}
			>
				{children}
			</AccessManagementProvider>
		);
	};

	return (
		<Suspense fallback={<ValanzLoaderComponent />}>
			<LazyMuiReactBootstrap
				authenticationOptions={authenticationOptions}
				theme={cutomOrangeTheme}
				extraProviders={[BackgroundWrapper]}
			>
				<AccessManagement>
					<ReactRootLayoutComponent />
				</AccessManagement>
			</LazyMuiReactBootstrap>
		</Suspense>
	);
};

export default App;

const ReactRootLayoutComponent = () => {
	const {
		getModules,
		hasModuleAccess,
		profileName,
		userRole,
		profileImage,
		isHooksUpdated,
	} = useAccessManagement();

	const [layoutOptions, setlayoutOptions] = useState({
		headerVariant: HeaderVariant.StickyHeader,
		appVersion: appVersion,
		logo: Logo,
		logoSmall: LogoSmall,
		userConfiguration: itemData.userConfiguration,
		headerMenuItems: homeMenuItems(hasModuleAccess),
		loaderComponent: () => <ValanzLoader />,
		layoutConfiguration: layoutConfig,
	});

	const [acessibleRoutes, setAcessibleRoutes] = useState([
		{
			path: "/",
			element: <ValanzLoaderComponent />,
		},
		{
			path: RouteConstants.ErrorUserNotFound,
			element: <Error403 />,
		},
		{
			path: RouteConstants.ErrorUnauthorized,
			element: <Error401 />,
		},
	]);

	const [accessibleStandaloneRoutes, setStandaloneRoutes] = useState<
		{
			path: string;
			element: React.ReactElement;
			module: any;
		}[]
	>([]);

	const sideEffect = isHooksUpdated + (profileName ?? "");

	useEffect(() => {
		isHooksUpdated && setAcessibleRoutes(routes(hasModuleAccess));

		const userInfoItem = {
			title: profileName || "",
			designation: userRole || "",
			profileImage: profileImage || undefined,
		};

		const userConfiguration: IUserInfoItem = {
			items: [profileListItem],
			isTabView: false,
			userPersonaldata: userInfoItem,
		};

		setlayoutOptions({
			headerVariant: HeaderVariant.StickyHeader,
			appVersion: appVersion,
			logo: Logo,
			logoSmall: LogoSmall,
			userConfiguration: userConfiguration,
			headerMenuItems: homeMenuItems(hasModuleAccess),
			loaderComponent: () => <ValanzLoader />,
			layoutConfiguration: layoutConfig,
		});

		setStandaloneRoutes(standaloneRoutes(hasModuleAccess));
	}, [sideEffect]);

	return (
		<Suspense fallback={<ValanzLoaderComponent />}>
			<LazyAppLayout
				layout={layoutOptions}
				routes={acessibleRoutes}
				standaloneRoutes={accessibleStandaloneRoutes}
			/>
		</Suspense>
	);
};
